import { useState, useEffect, useCallback } from 'react';
import { Globe, Send, CheckCircle, XCircle, Clock, Loader2, RefreshCw, Link2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../utils/api';

const API = `${BACKEND_URL}/api`;

export default function AdminGoogleIndexing() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [singleUrl, setSingleUrl] = useState('');
  const [submittingSingle, setSubmittingSingle] = useState(false);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/indexing/status`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      setStats(await res.json());
    } catch {
      toast.error('Failed to load indexing stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleBatch = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/indexing/batch`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (data.submitted > 0) toast.success(`Submitted ${data.submitted} URLs to Google`);
      else toast.info(data.message || 'No new URLs to submit');
      fetchStats();
    } catch { toast.error('Batch submission failed'); }
    finally { setSubmitting(false); }
  };

  const handleSingle = async (e) => {
    e.preventDefault();
    if (!singleUrl.trim()) return;
    setSubmittingSingle(true);
    try {
      const res = await fetch(`${API}/admin/indexing/submit`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ url: singleUrl.trim() }),
      });
      const data = await res.json();
      if (data.success) { toast.success(`Submitted: ${singleUrl}`); setSingleUrl(''); }
      else toast.error(`Failed: ${data.error || data.detail || 'Unknown error'}`);
      fetchStats();
    } catch { toast.error('Submission failed'); }
    finally { setSubmittingSingle(false); }
  };

  const handleRemove = async (url) => {
    try {
      const res = await fetch(`${API}/admin/indexing/remove`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (data.success) toast.success(`Removal requested: ${url}`);
      else toast.error('Removal failed');
      fetchStats();
    } catch { toast.error('Removal failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12" data-testid="indexing-loading">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );

  const pct = stats ? Math.round(((stats.today_submitted || 0) / 200) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-google-indexing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-bold text-slate-900">Google Indexing</h2>
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">200/day limit</span>
        </div>
        <button onClick={fetchStats} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="refresh-stats-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Today', value: stats?.today_submitted || 0, sub: `${stats?.today_remaining || 200} remaining`, icon: Send, color: 'text-blue-500' },
          { label: 'This Week', value: stats?.week_submitted || 0, icon: Clock, color: 'text-indigo-500' },
          { label: 'All Time', value: stats?.total_submitted || 0, icon: CheckCircle, color: 'text-green-500' },
          { label: 'Site Pages', value: stats?.total_site_pages || 0, icon: Globe, color: 'text-purple-500' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border p-4 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            <div className="text-2xl font-bold text-slate-900">{value.toLocaleString()}</div>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      {/* Daily quota bar */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex justify-between text-sm text-slate-500 mb-2">
          <span>Daily Quota</span>
          <span>{stats?.today_submitted || 0} / 200</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Batch Submit</h3>
          <p className="text-xs text-slate-400 mb-4">Submits all unindexed pages to Google. Skips URLs submitted in the last 7 days.</p>
          <button
            onClick={handleBatch}
            disabled={submitting || (stats?.today_remaining || 0) <= 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-gray-900 text-sm font-medium transition"
            data-testid="batch-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? 'Submitting...' : 'Submit All Unindexed URLs'}
          </button>
        </div>

        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Submit Single URL</h3>
          <form onSubmit={handleSingle} className="flex gap-2 mt-4">
            <input
              type="url" value={singleUrl} onChange={(e) => setSingleUrl(e.target.value)}
              placeholder="https://web-techservices.com/page"
              className="flex-1 px-3 py-2 rounded-lg border text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              data-testid="single-url-input"
            />
            <button type="submit" disabled={submittingSingle || !singleUrl.trim()}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 text-gray-900 text-sm font-medium transition"
              data-testid="single-submit-btn"
            >
              {submittingSingle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </div>

      {/* Recent Submissions */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          Recent Submissions <span className="text-slate-400 font-normal">(last 20)</span>
        </h3>
        {!stats?.recent_submissions?.length ? (
          <p className="text-slate-400 text-sm">No submissions yet.</p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {stats.recent_submissions.map((log, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition" data-testid={`submission-row-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {log.status === 'success'
                    ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    : <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
                  <span className="text-sm text-slate-700 truncate">{log.url.replace('https://web-techservices.com', '')}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4">
                  <span className="text-xs text-slate-400">{new Date(log.submitted_at).toLocaleDateString()}</span>
                  <button onClick={() => handleRemove(log.url)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition" title="Remove from index">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
